import {
  Component,
  Prop,
  State,
  Element,
  h,
  Listen,
  Watch,
} from '@stencil/core';
import { searchOverflowToggler } from '../../../../utils/helpers/overflowToggler';
import { getChatVisibilitySessionStorageValue, removeChatVisibilitySessionStorageValue } from '../conversational-search-logic/storage-manager/storage-manager-helpers';
import type { labelProviderInterface } from '../../../LabelProviderInterface';
import { focusNavigationElement } from '../conversational-search-logic/utils/utils';
import { Brands } from '../../../../constants/brands';

@Component({
  tag: 'uiaas-search-widget-conversational-search-chat-widget',
  styleUrl: 'conversational-search-chat-widget.scss',
  shadow: true,
})
export class WidgetConversationalSearchChat {
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly language: string;
  @Prop() readonly country: string;
  @Prop() readonly searchQuery: string;
  @Prop() readonly apigeeAuth: string;
  @Prop() readonly apigeeTokenUrl: string;
  @Prop() readonly conversationalSearchProductsEndpoint: string;
  @Prop() readonly conversationalSearchMedaliaFormId: string;
  @Prop() readonly conversationalSearchBrandUrl: string;
  @Prop() readonly brand?: string;

  @State() isChatVisible = false;
  @State() isChatVisibleOnAction: string;
  @State() initialFocus = false;
  @State() isGuidedSearchInvoked = false;

  private overlayOpeningTimeout;
  private isMultiAccount = false;
  private isMegaMenuOpen = false;


  @Element() el;

  @Listen('conversational-search:terms-and-conditions-modal', {
    target: 'window',
  })
  handleModalExit(e) {
      if (!e.detail.isModalVisible) this.initialFocus = true;
  }

  @Listen('popstate', { target: 'window' })
  @Listen('conversational-search:chat-visibility', { target: 'window' })
  @Listen('conversational-search:cta-action', { target: 'window' })
  @Listen('conversational-search:user-end-of-conversation', { target: 'window' })
  handleConversationalSearchVisibilityForNavigator(e: CustomEvent) {
      this.isChatVisible = e.detail?.isChatVisible;

      if (!this.isChatVisible) focusNavigationElement();

      // Can not use here boolean, as undefined will return falsy value and trigger hidden chat logic.
      this.isChatVisibleOnAction = e.detail?.isChatVisible ? 'ON' : 'OFF';
  }

  // workaround for mySchnider to avoid double scroll
  private handleChatVisibility(hided: boolean) {
      const extendedContent = document.querySelector('.extendedContent') as HTMLElement || null;
      if(extendedContent !== null || extendedContent !== undefined){
        extendedContent.style.minHeight = hided ? '75vh' : '0';
      }

      const pageContainer = document.querySelector('.page-container') as HTMLElement || null ;
      if(pageContainer !== null || pageContainer !== undefined){
        pageContainer.style.minHeight = hided ? '70vh' : '0';;
      }

      const searchContainer = document.querySelector('.search-component-container') as HTMLElement || null;
      if(searchContainer !== null || searchContainer !== undefined){
        searchContainer.style.minHeight = hided ? '100vh' : '0';;
      }
      
      const dashboard = document.querySelector('dashboard-tab-view-v2') as HTMLElement || null;
      if(dashboard !== null || dashboard !== undefined){
        dashboard.style.display = hided ? 'block' : 'none';
      }
  }

  @Listen('conversational-search:cta-action', { target: 'window' })
  handleCtaInvokePlacement(e: CustomEvent) {
      this.isGuidedSearchInvoked = e.detail?.isGuidedSearch;
  }

  @Listen('keydown', { target: 'window' })
  handleTabPress(e) {
      // Move user tab navigation to chat modal, without it tab navigation go under chat (SRP elements)!
      if (e.key === 'Tab' && this.initialFocus) {
        this.el.shadowRoot.querySelector('.conversational-search-chat').focus();
        this.initialFocus = false;
      }
  }

  // Delay overflow body change in order to avoid visible components movement
  private openChatWithDelay() {
      this.overlayOpeningTimeout = setTimeout(() => {
          searchOverflowToggler(true);
      }, 400);
  }

  @Watch('isChatVisible')
  handleBodyOverflowClass() {
      if (this.isChatVisible) {
        this.openChatWithDelay();
        this.initialFocus = true;
      } else {
        clearTimeout(this.overlayOpeningTimeout);
        searchOverflowToggler(false);
        this.initialFocus = false;
      }
      if(this.brand === Brands.MYSCHNEIDER){
        this.handleChatVisibility(!this.isChatVisible);
      }
  }

  @Watch('searchQuery')
  handleQueryChange() {
      if (this.isChatVisible) {
        this.isChatVisible = false;
        this.isChatVisibleOnAction = 'OFF';
      }
  }

  componentWillLoad() {
      if(this.brand === Brands.MYSCHNEIDER){
        this.isMultiAccount = document.querySelector('#accountid-select') !== null;
        const header = document.querySelector('qds-web-header');
        if (header) {
          const headerClass =
            header.shadowRoot?.querySelector('header')?.classList[0];
          this.isMegaMenuOpen = headerClass !== 'shadow';
        }
      }
      
      if (getChatVisibilitySessionStorageValue()) {
        this.isChatVisible = true;
        removeChatVisibilitySessionStorageValue(); // remove after loading Gudied Search case
      }
  }

  disconnectedCallback() {
      clearTimeout(this.overlayOpeningTimeout);
  }

  render() {
      if (this.isChatVisible) this.openChatWithDelay();

      return (
        <div
          tabIndex={1}
          data-is-component-visibile={this.isChatVisible}
          class={{
            'conversational-search-chat': true,
            'conversational-search-chat--animation-visible': this.isChatVisibleOnAction === 'ON',
            'conversational-search-chat--animation-hidden': this.isChatVisibleOnAction === 'OFF',
            'conversational-search-chat--hidden': !this.isChatVisible && !this.isChatVisibleOnAction
          }}
        >
          <div class=
            {{
              'conversational-search-chat-container': true,
              'conversational-search-chat-container-menu-open': (this.brand === Brands.MYSCHNEIDER && this.isMegaMenuOpen && !this.isMultiAccount),
              'conversational-search-chat-container-menu-not-open': (this.brand === Brands.MYSCHNEIDER && !this.isMegaMenuOpen && !this.isMultiAccount),
              'conversational-search-chat-container-menu-open-multi-account': (this.brand === Brands.MYSCHNEIDER && this.isMegaMenuOpen && this.isMultiAccount),
              'conversational-search-chat-container-menu-not-open-multi-account': (this.brand === Brands.MYSCHNEIDER && !this.isMegaMenuOpen && this.isMultiAccount),
            }}>
            <div class="conversational-search-chat-content">
              <uiaas-conversational-search-chat-widget-body
                labelProvider={(e, f) => this.labelProvider(e, f)}
                country={this.country}
                language={this.language}
                searchQuery={this.searchQuery}
                apigeeAuth={this.apigeeAuth}
                apigeeTokenUrl={this.apigeeTokenUrl}
                conversationalSearchProductsEndpoint={this.conversationalSearchProductsEndpoint}
                conversationalSearchMedaliaFormId={this.conversationalSearchMedaliaFormId}
                conversationalSearchBrandUrl={this.conversationalSearchBrandUrl}
                brand={this.brand}
              />
            </div>
          </div>
        </div>
      );
  }
}
